/*
 * Leader election for the outbox (spec 08 §6.12, protocol.md §2.12): of all the tabs of one
 * session exactly one dispatches, and it is whichever holds the Web Lock `teambase-outbox`. A
 * lock is released by the browser when its tab dies, which is what makes a killed leader hand
 * over without a heartbeat; a tab entering the back-forward cache is not dead, so it gives the
 * lock back on `pagehide` and asks for it again on `pageshow`.
 *
 * Leading is the right to dispatch, so only a context that *can* dispatch may hold it: the owner
 * says `compete()` while it has a connection and `standDown()` while it has none, and a tab whose
 * socket is gone — hidden for five minutes, offline, signed out — never sits on the lock in front
 * of one that could send. A hidden tab that keeps its socket (`hiddenKeep.ts`) keeps the lead, and
 * sends: another tab's message reaches it over the channel.
 *
 * Every request for the lock is a **bid** of its own, with its own abort signal and its own
 * release. Withdrawing a bid forgets it at once, whatever the lock manager still owes it — the
 * rejection of an aborted request, a grant that was already on its way — so the next `compete()`
 * is never refused because the last withdrawal has not settled, and a late answer to an old bid
 * can touch nothing of the new one.
 *
 * Without a lock manager — an old engine, a test — the one context there is leads at once.
 */

export const OUTBOX_LOCK = "teambase-outbox"

/** The slice of `navigator.locks` this file uses; a test passes a fake with two contexts. */
export interface LeaderLocks {
	request(
		name: string,
		options: { mode: "exclusive"; signal: AbortSignal },
		callback: () => Promise<void>,
	): Promise<void>
}

export interface LeadershipOptions {
	/** `undefined` means "the browser's", `null` means "no election: whoever competes leads". */
	locks?: LeaderLocks | null
	name?: string
	onAcquire(): void
	/** Called before the lock goes back, so a successor never leads beside a context that still believes it does. */
	onRelease(): void
}

export interface Leadership {
	/** Asks for the lock and keeps asking across the back-forward cache; harmless when repeated. */
	compete(): void
	/** Gives the lock back, or leaves the queue for it, until the next `compete()`. */
	standDown(): void
	/** Stands down for good and lets go of the page. */
	stop(): void
}

/** One request for the lock, from the asking to the giving back. */
interface Bid {
	readonly controller: AbortController
	/** Gives the lock back; present exactly while this bid holds it. */
	release: (() => void) | undefined
	withdrawn: boolean
}

/** The election of a context that has nobody to share with: every request is granted on the spot. */
const NO_ELECTION: LeaderLocks = {
	request: (_name, _options, callback) => callback(),
}

/** Prepares this context's part in the election; nothing is asked for before `compete()`. */
export function holdLeadership(opts: LeadershipOptions): Leadership {
	const locks = (opts.locks === undefined ? browserLocks() : opts.locks) ?? NO_ELECTION
	const name = opts.name ?? OUTBOX_LOCK
	let stopped = false
	let wanted = false
	let bid: Bid | undefined

	const enter = () => {
		if (stopped || !wanted || bid !== undefined) return
		const mine: Bid = { controller: new AbortController(), release: undefined, withdrawn: false }
		bid = mine
		locks
			.request(name, { mode: "exclusive", signal: mine.controller.signal }, async () => {
				// Withdrawn after the grant was decided: an abort comes too late for that, so the
				// lock is handed straight back instead of being held by a context nobody can tell.
				if (mine.withdrawn) return
				const held = new Promise<void>((resolve) => {
					mine.release = resolve
				})
				opts.onAcquire()
				await held
			})
			.catch(() => {
				// An aborted request while waiting; nothing was held.
			})
	}

	const withdraw = () => {
		const mine = bid
		if (mine === undefined) return
		bid = undefined
		mine.withdrawn = true
		if (mine.release === undefined) {
			mine.controller.abort()
			return
		}
		opts.onRelease()
		mine.release()
	}

	window.addEventListener("pagehide", withdraw)
	window.addEventListener("pageshow", enter)

	return {
		compete() {
			wanted = true
			enter()
		},
		standDown() {
			wanted = false
			withdraw()
		},
		stop() {
			stopped = true
			window.removeEventListener("pagehide", withdraw)
			window.removeEventListener("pageshow", enter)
			withdraw()
		},
	}
}

function browserLocks(): LeaderLocks | null {
	const locks = (navigator as Partial<Navigator>).locks
	return locks !== undefined && typeof locks.request === "function" ? locks : null
}
